// src/components/games/crossroads/aiPlayer.ts
import { logger } from '../../../utils/logger';
// AI system for Crossroads TCG

import {
  GameState,
  PlayerState,
  Card,
  CreatureCard,
  ArtCard,
  ManaSourceCard,
  ElementType,
  CardType,
  AbilityKeyword,
  ManaCost,
  BattlefieldCreature,
} from './types';
import {
  playManaSource,
  playCreature,
  playArt,
  tapManaSource,
  drawCards,
  canAffordCost,
  activateCommanderAbility,
} from './gameLogic';

// ============================================================================
// DIFFICULTY LEVELS
// ============================================================================

export enum AIDifficulty {
  EASY = 'easy',
  MEDIUM = 'medium',
  HARD = 'hard',
}

/**
 * Shuffle an array (Fisher-Yates algorithm)
 */
function shuffleArray<T>(array: T[]): T[] {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

// ============================================================================
// EVALUATION HELPER FUNCTIONS
// ============================================================================

/**
 * Calculate total board strength for a player
 * Returns sum of power + health of all creatures
 */
export function evaluateBoardStrength(player: PlayerState, game: GameState): number {
  return player.battlefield.creatures.reduce((total, creature) => {
    return total + creature.power + creature.health;
  }, 0);
}

/**
 * Calculate the threat level of an opponent
 * Higher score = more dangerous opponent
 */
export function evaluateThreatLevel(
  opponentId: number,
  game: GameState,
  myId: number
): number {
  const opponent = game.players[opponentId];
  let threat = 0;

  // Board strength is major threat
  threat += evaluateBoardStrength(opponent, game) * 2;

  // GM health difference
  const myHealth = game.players[myId].commander.currentHealth;
  const theirHealth = opponent.commander.currentHealth;
  threat += (myHealth - theirHealth) * 0.5;

  // Number of cards in hand (potential plays)
  threat += opponent.hand.length * 3;

  // Available mana
  threat += Object.values(opponent.manaPool).reduce((sum, val) => sum + val, 0) * 2;

  return threat;
}

/**
 * Evaluate the value of a creature card
 */
export function evaluateCreatureValue(card: CreatureCard): number {
  let value = card.power * 2 + card.health * 1.5;

  // Bonus for keywords (more comprehensive)
  if (card.keywords.includes(AbilityKeyword.FLYING)) value += 4; // Evasion is powerful
  if (card.keywords.includes(AbilityKeyword.FIRST_STRIKE)) value += 3; // Combat advantage
  if (card.keywords.includes(AbilityKeyword.DOUBLE_STRIKE)) value += 6; // Very powerful
  if (card.keywords.includes(AbilityKeyword.DEATHTOUCH)) value += 4; // Kills anything
  if (card.keywords.includes(AbilityKeyword.LIFELINK)) value += 3; // Life gain
  if (card.keywords.includes(AbilityKeyword.BLOCK)) value += 2; // Defensive
  if (card.keywords.includes(AbilityKeyword.HASTE)) value += 2; // Immediate impact
  if (card.keywords.includes(AbilityKeyword.VIGILANCE)) value += 2; // Attack + defend
  if (card.keywords.includes(AbilityKeyword.TRAMPLE)) value += 2; // Damage overflow

  // Bonus for abilities
  if (card.abilities && card.abilities.length > 0) {
    value += card.abilities.length * 2.5;
  }

  // Cost efficiency - lower cost is better for same stats
  const totalCost = Object.values(card.cost).reduce((sum, val) => sum + val, 0);
  if (totalCost > 0) {
    value += (value / totalCost) * 2; // Efficiency bonus
  }

  return value;
}

/**
 * Calculate total mana available in pool
 */
export function getTotalMana(player: PlayerState): number {
  return Object.values(player.manaPool).reduce((sum, val) => sum + val, 0);
}

/**
 * Check if we can afford a mana cost
 */
export function canAfford(cost: ManaCost, player: PlayerState): boolean {
  for (const [element, amount] of Object.entries(cost)) {
    const available = player.manaPool[element as ElementType] || 0;
    if (available < amount) return false;
  }
  return true;
}

/**
 * Find the best creature to attack with
 * Prioritizes high power, flying for evasion
 */
export function findBestAttacker(
  player: PlayerState,
  game: GameState
): BattlefieldCreature | null {
  const available = player.battlefield.creatures.filter(
    (c) => !c.isTapped && !c.summoningSick
  );

  if (available.length === 0) return null;

  // Sort by value (power + flying bonus)
  available.sort((a, b) => {
    const aFlying = a.keywords.includes(AbilityKeyword.FLYING) ? 5 : 0;
    const bFlying = b.keywords.includes(AbilityKeyword.FLYING) ? 5 : 0;
    return b.power + bFlying - (a.power + aFlying);
  });

  return available[0];
}

/**
 * Find the best blocker against an attacker
 */
export function findBestBlocker(
  attacker: BattlefieldCreature,
  defenders: BattlefieldCreature[],
  game: GameState
): BattlefieldCreature | null {
  // Filter available blockers
  const available = defenders.filter((d) => !d.isTapped);

  if (available.length === 0) return null;

  // Can't block flying without flying
  const attackerFlying = attacker.keywords.includes(AbilityKeyword.FLYING);
  const validBlockers = available.filter((d) => {
    if (attackerFlying) {
      return d.keywords.includes(AbilityKeyword.FLYING);
    }
    return true;
  });

  if (validBlockers.length === 0) return null;

  // Prefer blockers that:
  // 1. Can kill the attacker without dying
  // 2. Can survive the attack
  // 3. Have combat keywords advantage
  // 4. Have lowest value (sacrifice weakest)

  const scored = validBlockers.map((blocker) => {
    let score = 0;

    const blockerHasDeathtouch = blocker.keywords.includes(AbilityKeyword.DEATHTOUCH);
    const blockerHasFirstStrike = blocker.keywords.includes(AbilityKeyword.FIRST_STRIKE);
    const attackerHasFirstStrike = attacker.keywords.includes(AbilityKeyword.FIRST_STRIKE);
    const attackerHasDeathtouch = attacker.keywords.includes(AbilityKeyword.DEATHTOUCH);

    // Deathtouch blocker kills anything
    if (blockerHasDeathtouch) score += 25;

    // First strike advantage
    if (blockerHasFirstStrike && !attackerHasFirstStrike) score += 15;
    if (!blockerHasFirstStrike && attackerHasFirstStrike) score -= 10;

    // Can kill attacker?
    if (blocker.power >= attacker.health) score += 20;

    // Will survive?
    if (blocker.health > attacker.power && !attackerHasDeathtouch) score += 15;

    // Trade favorably (even trade or better)
    if (blocker.power >= attacker.health && attacker.power >= blocker.health) {
      score += 10; // Favorable trade
    }

    // Avoid blocking with valuable creatures unless necessary
    score -= (blocker.power + blocker.health) * 0.5;

    return { blocker, score };
  });

  scored.sort((a, b) => b.score - a.score);
  return scored[0].blocker;
}

/**
 * Determine if we should attack based on board state
 */
export function shouldAttack(
  myPlayer: PlayerState,
  opponentPlayer: PlayerState,
  game: GameState,
  difficulty: AIDifficulty
): boolean {
  const myStrength = evaluateBoardStrength(myPlayer, game);
  const opponentStrength = evaluateBoardStrength(opponentPlayer, game);
  const myHealth = myPlayer.commander.currentHealth;
  const oppHealth = opponentPlayer.commander.currentHealth;

  // Easy: Random chance
  if (difficulty === AIDifficulty.EASY) {
    return Math.random() > 0.5;
  }

  // Medium: Attack if we have advantage, but be cautious when low on health
  if (difficulty === AIDifficulty.MEDIUM) {
    // Play defensive if we're low on health
    if (myHealth <= 5) {
      return myStrength > opponentStrength * 1.5;
    }
    return myStrength >= opponentStrength * 0.8;
  }

  // Hard: Strategic decision
  const canWin = myPlayer.battlefield.creatures.some((c) => {
    return (
      !c.isTapped &&
      !c.summoningSick &&
      c.power >= oppHealth
    );
  });

  // Always attack for lethal
  if (canWin) return true;

  // Play defensive when low on health
  if (myHealth <= 5) {
    return myStrength > opponentStrength * 2.0; // Need overwhelming advantage
  }

  if (myHealth <= 10) {
    return myStrength > opponentStrength * 1.5; // Need strong advantage
  }

  // Attack if we have board advantage
  return myStrength > opponentStrength * 1.2;
}

// ============================================================================
// TARGETING LOGIC
// ============================================================================

/**
 * Find best target for damage effects
 */
export function findBestDamageTarget(
  game: GameState,
  playerId: number,
  damage: number,
  targetType: 'creature' | 'player' | 'any'
): { type: 'creature' | 'player'; targetId: string } | null {
  const opponentId = playerId === 0 ? 1 : 0;
  const opponent = game.players[opponentId];

  if (targetType === 'player' || targetType === 'any') {
    // Can we kill opponent GM?
    if (opponent.commander.currentHealth <= damage) {
      return { type: 'player', targetId: `player_${opponentId}` };
    }
  }

  if (targetType === 'creature' || targetType === 'any') {
    // Find creatures we can kill
    const killable = opponent.battlefield.creatures.filter(
      (c) => c.health <= damage
    );

    if (killable.length > 0) {
      // Kill highest value creature
      killable.sort((a, b) => b.power + b.health - (a.power + a.health));
      return { type: 'creature', targetId: killable[0].instanceId };
    }

    // Otherwise damage highest threat
    if (opponent.battlefield.creatures.length > 0) {
      const creatures = [...opponent.battlefield.creatures];
      creatures.sort((a, b) => b.power - a.power);
      return { type: 'creature', targetId: creatures[0].instanceId };
    }
  }

  // Default to opponent GM
  if (targetType === 'player' || targetType === 'any') {
    return { type: 'player', targetId: `player_${opponentId}` };
  }

  return null;
}

/**
 * Find best friendly target for buff effects
 */
export function findBestBuffTarget(
  game: GameState,
  playerId: number,
  powerBuff: number,
  healthBuff: number
): string | null {
  const player = game.players[playerId];

  if (player.battlefield.creatures.length === 0) return null;

  // Prefer creatures that are attacking
  const attackers = player.battlefield.creatures.filter((c) => c.isTapped);
  if (attackers.length > 0) {
    // Buff biggest attacker
    attackers.sort((a, b) => b.power - a.power);
    return attackers[0].instanceId;
  }

  // Otherwise buff biggest creature
  const creatures = [...player.battlefield.creatures];
  creatures.sort((a, b) => b.power + b.health - (a.power + a.health));
  return creatures[0].instanceId;
}

/**
 * Find best creature to sacrifice
 */
export function findBestSacrificeTarget(
  game: GameState,
  playerId: number
): string | null {
  const player = game.players[playerId];

  if (player.battlefield.creatures.length === 0) return null;

  // Sacrifice weakest creature
  const creatures = [...player.battlefield.creatures];
  creatures.sort((a, b) => a.power + a.health - (b.power + b.health));
  return creatures[0].instanceId;
}

// ============================================================================
// CARD PLAY DECISION LOGIC
// ============================================================================

/**
 * Decide which mana source to play
 * Returns the card ID of the best mana source to play, or null
 */
export function decideManaSourceToPlay(
  game: GameState,
  playerId: number,
  difficulty: AIDifficulty
): string | null {
  const player = game.players[playerId];

  if (player.hasPlayedManaThisTurn) return null;

  // Get all mana sources in hand that we can afford to play
  const manaSources = player.hand
    .map((cardId) => game.cardInstances[cardId])
    .filter((card) => {
      if (card?.type !== CardType.MANA_SOURCE) return false;
      const manaCard = card as ManaSourceCard;
      // Check if we can afford the cost (basic mana sources have no cost)
      if (!manaCard.cost || Object.keys(manaCard.cost).length === 0) return true;
      return canAfford(manaCard.cost, player);
    }) as ManaSourceCard[];

  if (manaSources.length === 0) return null;

  // Easy: Play first affordable one
  if (difficulty === AIDifficulty.EASY) {
    return manaSources[0].id;
  }

  // Medium/Hard: Play mana source that generates most mana
  manaSources.sort((a, b) => {
    const aTotal = Object.values(a.manaGenerated).reduce((sum, val) => sum + val, 0);
    const bTotal = Object.values(b.manaGenerated).reduce((sum, val) => sum + val, 0);
    return bTotal - aTotal;
  });

  return manaSources[0].id;
}

/**
 * Decide which creature to play
 * Returns the card ID of the best creature to play, or null
 */
export function decideCreatureToPlay(
  game: GameState,
  playerId: number,
  difficulty: AIDifficulty
): string | null {
  const player = game.players[playerId];

  // Get all creatures in hand we can afford
  const creatures = player.hand
    .map((cardId) => game.cardInstances[cardId])
    .filter((card) => {
      if (card?.type !== CardType.CREATURE) return false;
      return canAfford(card.cost, player);
    }) as CreatureCard[];

  if (creatures.length === 0) return null;

  // Easy: Play random affordable creature
  if (difficulty === AIDifficulty.EASY) {
    return creatures[Math.floor(Math.random() * creatures.length)].id;
  }

  // Medium/Hard: Play highest value creature
  creatures.sort((a, b) => {
    return evaluateCreatureValue(b) - evaluateCreatureValue(a);
  });

  return creatures[0].id;
}

/**
 * Decide which art (spell) to play
 * Returns the card ID of the best art to play, or null
 */
export function decideArtToPlay(
  game: GameState,
  playerId: number,
  difficulty: AIDifficulty
): string | null {
  const player = game.players[playerId];
  const opponentId = playerId === 0 ? 1 : 0;
  const opponent = game.players[opponentId];

  // Get all arts in hand we can afford
  const arts = player.hand
    .map((cardId) => game.cardInstances[cardId])
    .filter((card) => {
      if (card?.type !== CardType.ART) return false;
      return canAfford(card.cost, player);
    }) as ArtCard[];

  if (arts.length === 0) return null;

  // Easy: Random art
  if (difficulty === AIDifficulty.EASY) {
    if (Math.random() > 0.5) {
      return arts[Math.floor(Math.random() * arts.length)].id;
    }
    return null;
  }

  // Medium/Hard: Strategic art usage
  // Prioritize removal if opponent has threats
  const opponentHasThreats = opponent.battlefield.creatures.some(
    (c) => c.power >= 3 || c.keywords.includes(AbilityKeyword.FLYING)
  );

  // Look for removal spells if opponent has threats
  if (opponentHasThreats) {
    const removalArts = arts.filter((art) =>
      art.name.toLowerCase().includes('destroy') ||
      art.name.toLowerCase().includes('damage') ||
      art.name.toLowerCase().includes('kill')
    );

    if (removalArts.length > 0) {
      return removalArts[0].id;
    }
  }

  // Look for draw spells if hand is low
  if (player.hand.length <= 3) {
    const drawArts = arts.filter((art) =>
      art.name.toLowerCase().includes('draw')
    );

    if (drawArts.length > 0) {
      return drawArts[0].id;
    }
  }

  // Play most expensive art we can afford (usually most powerful)
  arts.sort((a, b) => {
    const aCost = Object.values(a.cost).reduce((sum, val) => sum + val, 0);
    const bCost = Object.values(b.cost).reduce((sum, val) => sum + val, 0);
    return bCost - aCost;
  });

  return arts[0].id;
}

/**
 * Decide if AI should mulligan (discard hand and redraw)
 * Only mulligan if hand has no basic mana sources
 */
export function decideIfShouldMulligan(
  game: GameState,
  playerId: number,
  difficulty: AIDifficulty
): boolean {
  const player = game.players[playerId];

  // Check if hand has any basic mana sources (no cost)
  const hasBasicMana = player.hand.some(cardId => {
    const card = game.cardInstances[cardId];
    if (!card || card.type !== CardType.MANA_SOURCE) return false;
    const manaCard = card as ManaSourceCard;
    return !manaCard.cost || Object.keys(manaCard.cost).length === 0;
  });

  // Easy AI: Never mulligan (too passive)
  if (difficulty === AIDifficulty.EASY) {
    return false;
  }

  // Medium/Hard: Mulligan if no basic mana
  return !hasBasicMana;
}

/**
 * Decide which GM ability to use
 */
export function decideGMAbilityToUse(
  game: GameState,
  playerId: number,
  difficulty: AIDifficulty
): string | null {
  const player = game.players[playerId];
  const commander = game.cardInstances[player.commander.cardId];

  if (commander?.type !== CardType.COMMANDER) return null;

  // Find affordable abilities
  const affordableAbilities = commander.abilities.filter((ability) => {
    const used = player.commander.abilityUsesThisTurn[ability.id] || 0;
    const maxUses = ability.usesPerTurn || Infinity;
    if (used >= maxUses) return false;

    return canAfford(ability.cost, player);
  });

  if (affordableAbilities.length === 0) return null;

  // Easy: Random
  if (difficulty === AIDifficulty.EASY) {
    if (Math.random() > 0.7) {
      return affordableAbilities[Math.floor(Math.random() * affordableAbilities.length)].id;
    }
    return null;
  }

  // Medium/Hard: Use first available ability
  // (GM abilities are usually beneficial, so use them when we can)
  return affordableAbilities[0].id;
}

// ============================================================================
// COMBAT DECISION LOGIC
// ============================================================================

/**
 * Decide which creatures should attack
 * Returns array of creature instance IDs
 */
export function decideAttackers(
  game: GameState,
  playerId: number,
  difficulty: AIDifficulty
): string[] {
  const player = game.players[playerId];
  const opponentId = playerId === 0 ? 1 : 0;
  const opponent = game.players[opponentId];

  const availableAttackers = player.battlefield.creatures.filter(
    (c) => !c.isTapped && !c.summoningSick
  );

  if (availableAttackers.length === 0) return [];

  // Decide if we should attack at all
  if (!shouldAttack(player, opponent, game, difficulty)) {
    return [];
  }

  // Easy: Attack with random creatures
  if (difficulty === AIDifficulty.EASY) {
    return availableAttackers
      .filter(() => Math.random() > 0.5)
      .map((c) => c.instanceId);
  }

  // Medium: Attack with flying, strong creatures, and evasive threats
  if (difficulty === AIDifficulty.MEDIUM) {
    return availableAttackers
      .filter((c) => {
        const hasEvasion = c.keywords.includes(AbilityKeyword.FLYING);
        const hasTrample = c.keywords.includes(AbilityKeyword.TRAMPLE);
        const hasVigilance = c.keywords.includes(AbilityKeyword.VIGILANCE);
        const isStrong = c.power >= 3;

        return hasEvasion || hasTrample || hasVigilance || isStrong;
      })
      .map((c) => c.instanceId);
  }

  // Hard: Strategic attacking
  const myStrength = evaluateBoardStrength(player, game);
  const oppStrength = evaluateBoardStrength(opponent, game);

  // All-in attack if we have overwhelming advantage
  if (myStrength > oppStrength * 1.5) {
    return availableAttackers.map((c) => c.instanceId);
  }

  // Selective attack - prioritize evasive and powerful creatures
  return availableAttackers
    .filter((c) => {
      const hasFlying = c.keywords.includes(AbilityKeyword.FLYING);
      const hasTrample = c.keywords.includes(AbilityKeyword.TRAMPLE);
      const hasVigilance = c.keywords.includes(AbilityKeyword.VIGILANCE);
      const hasFirstStrike = c.keywords.includes(AbilityKeyword.FIRST_STRIKE);
      const isStrong = c.power >= 4;
      const canKillGM = c.power >= opponent.commander.currentHealth;

      // Always attack with creatures that can win the game
      if (canKillGM) return true;

      // Always attack with vigilance (can still block)
      if (hasVigilance) return true;

      // Attack with evasive creatures
      if (hasFlying || hasTrample) return true;

      // Attack with first strike or strong creatures
      return hasFirstStrike || isStrong;
    })
    .map((c) => c.instanceId);
}

/**
 * Decide how to block attacking creatures
 * Returns map of attacker instanceId -> blocker instanceId
 */
export function decideBlockers(
  game: GameState,
  playerId: number,
  attackers: BattlefieldCreature[],
  difficulty: AIDifficulty
): Record<string, string> {
  const player = game.players[playerId];
  const blocks: Record<string, string> = {};

  // Easy: Random blocking
  if (difficulty === AIDifficulty.EASY) {
    attackers.forEach((attacker) => {
      if (Math.random() > 0.5) {
        const blocker = findBestBlocker(attacker, player.battlefield.creatures, game);
        if (blocker && !Object.values(blocks).includes(blocker.instanceId)) {
          blocks[attacker.instanceId] = blocker.instanceId;
        }
      }
    });
    return blocks;
  }

  // Medium/Hard: Smart blocking
  // Sort attackers by threat (most dangerous first)
  const sortedAttackers = [...attackers].sort((a, b) => {
    // Prioritize blocking lethal damage to GM
    const aCanKill = a.power >= player.commander.currentHealth;
    const bCanKill = b.power >= player.commander.currentHealth;
    if (aCanKill && !bCanKill) return -1;
    if (bCanKill && !aCanKill) return 1;

    // Then by power
    return b.power - a.power;
  });

  const usedBlockers = new Set<string>();

  sortedAttackers.forEach((attacker) => {
    const availableDefenders = player.battlefield.creatures.filter(
      (c) => !usedBlockers.has(c.instanceId)
    );

    const blocker = findBestBlocker(attacker, availableDefenders, game);
    if (blocker) {
      blocks[attacker.instanceId] = blocker.instanceId;
      usedBlockers.add(blocker.instanceId);
    }
  });

  return blocks;
}

// ============================================================================
// MAIN AI TURN EXECUTOR
// ============================================================================

/**
 * Execute a full AI turn
 * This is the main entry point for AI decision-making
 */
export async function executeAITurn(
  game: GameState,
  playerId: number,
  difficulty: AIDifficulty = AIDifficulty.MEDIUM
): Promise<GameState> {
  const currentGame = { ...game };
  const player = currentGame.players[playerId];

  // Check for mulligan on first turn
  if (currentGame.currentRound === 1 && !player.hasPlayedManaThisTurn &&
      player.battlefield.creatures.length === 0 &&
      player.battlefield.manaSources.length === 0) {
    const shouldMulligan = decideIfShouldMulligan(currentGame, playerId, difficulty);
    if (shouldMulligan) {
      logger.log(`AI ${playerId} taking mulligan`);
      // Shuffle hand back into deck
      player.deck.push(...player.hand);
      player.hand = [];
      player.deck = shuffleArray(player.deck);

      // Draw 7 new cards
      drawCards(currentGame, playerId, 7);

      // Skip turn by returning immediately
      return currentGame;
    }
  }

  // Phase 1: Tap mana sources for mana
  await tapManaSourcesForMana(currentGame, playerId, difficulty);

  // Phase 2: Use GM abilities
  await activateGMAbilities(currentGame, playerId, difficulty);

  // Phase 3: Play mana source (if haven't played one yet)
  const manaSourceToPlay = decideManaSourceToPlay(currentGame, playerId, difficulty);
  if (manaSourceToPlay) {
    const success = playManaSource(currentGame, playerId, manaSourceToPlay);
    if (!success) {
      logger.warn(`AI failed to play mana source: ${manaSourceToPlay}`);
    }
  }

  // Phase 4: Main phase - play creatures and arts
  await mainPhase(currentGame, playerId, difficulty);

  // Phase 5: Combat phase (if applicable based on game phase)
  // Combat will be handled by combat phase logic

  return currentGame;
}

/**
 * Tap mana sources to generate mana
 */
async function tapManaSourcesForMana(
  game: GameState,
  playerId: number,
  difficulty: AIDifficulty
): Promise<void> {
  const player = game.players[playerId];
  const untappedMana = player.battlefield.manaSources.filter((m) => !m.isTapped);

  // Easy/Medium: Tap all mana sources
  if (difficulty !== AIDifficulty.HARD) {
    for (const manaSource of untappedMana) {
      tapManaSource(game, playerId, manaSource.instanceId);
    }
    return;
  }

  // Hard: Be strategic about mana - save some for instant-speed responses
  // Tap enough mana to play our most expensive cards, but keep some untapped
  const maxNeeded = player.hand.reduce((max, cardId) => {
    const card = game.cardInstances[cardId];
    if (!card || !('cost' in card) || !card.cost) return max;
    const cost = Object.values(card.cost).reduce((sum: number, val: number) => sum + val, 0);
    return Math.max(max, cost);
  }, 0);

  // Tap mana up to what we need, leaving at least 1-2 sources untapped if possible
  const toTap = Math.max(0, Math.min(untappedMana.length - 2, maxNeeded));
  for (let i = 0; i < toTap; i++) {
    tapManaSource(game, playerId, untappedMana[i].instanceId);
  }
}

/**
 * Use GM abilities strategically
 */
async function activateGMAbilities(
  game: GameState,
  playerId: number,
  difficulty: AIDifficulty
): Promise<void> {
  // Try to use GM ability once per turn
  const abilityToUse = decideGMAbilityToUse(game, playerId, difficulty);

  if (abilityToUse) {
    // Execute the GM ability using the game logic function
    try {
      const success = activateCommanderAbility(game, playerId, abilityToUse);
      if (success) {
        logger.log(`AI used commander ability: ${abilityToUse}`);
      }
    } catch (error) {
      logger.error('AI commander ability execution error:', error);
    }
  }
}

/**
 * Main phase - play creatures and arts
 */
async function mainPhase(
  game: GameState,
  playerId: number,
  difficulty: AIDifficulty
): Promise<void> {
  const maxPlays = difficulty === AIDifficulty.EASY ? 2 : difficulty === AIDifficulty.MEDIUM ? 3 : 5;
  let plays = 0;

  // Keep trying to play cards until we can't afford anything or hit max plays
  while (plays < maxPlays) {
    const player = game.players[playerId];
    const totalMana = getTotalMana(player);

    if (totalMana === 0) break;

    let playedSomething = false;

    // Prioritize creatures over arts (build board presence)
    const creatureToPlay = decideCreatureToPlay(game, playerId, difficulty);
    if (creatureToPlay) {
      const success = playCreature(game, playerId, creatureToPlay);
      if (success) {
        playedSomething = true;
        plays++;
      }
      continue;
    }

    // Try to play art if no creatures available
    const artToPlay = decideArtToPlay(game, playerId, difficulty);
    if (artToPlay) {
      // Note: Art targeting would need to be handled by the targeting system
      // For now, we'll use the best target finding functions
      const artCard = game.cardInstances[artToPlay] as ArtCard;
      let success = false;

      if (artCard.targetType && artCard.targetType !== 'none') {
        const target = findBestDamageTarget(game, playerId, 3, artCard.targetType);
        if (target) {
          success = playArt(game, playerId, artToPlay, {
            creatures: target.type === 'creature' ? [target.targetId] : [],
            players: target.type === 'player' ? [parseInt(target.targetId.split('_')[1])] : [],
          });
        }
      } else {
        success = playArt(game, playerId, artToPlay);
      }

      if (success) {
        playedSomething = true;
        plays++;
      }
      continue;
    }

    // If we can't play anything, stop
    if (!playedSomething) break;
  }

}

/**
 * Get AI action for combat phase (attacking)
 */
export function getAIAttackDecision(
  game: GameState,
  playerId: number,
  difficulty: AIDifficulty = AIDifficulty.MEDIUM
): string[] {
  return decideAttackers(game, playerId, difficulty);
}

/**
 * Get AI action for combat phase (blocking)
 */
export function getAIBlockDecision(
  game: GameState,
  playerId: number,
  attackers: BattlefieldCreature[],
  difficulty: AIDifficulty = AIDifficulty.MEDIUM
): Record<string, string> {
  return decideBlockers(game, playerId, attackers, difficulty);
}

// ============================================================================
// EXPORTS
// ============================================================================

export default {
  // Difficulty
  AIDifficulty,

  // Main AI functions
  executeAITurn,
  getAIAttackDecision,
  getAIBlockDecision,

  // Helper functions
  evaluateBoardStrength,
  evaluateThreatLevel,
  evaluateCreatureValue,
  getTotalMana,
  canAfford,

  // Targeting
  findBestAttacker,
  findBestBlocker,
  shouldAttack,
  findBestDamageTarget,
  findBestBuffTarget,
  findBestSacrificeTarget,

  // Decisions
  decideManaSourceToPlay,
  decideCreatureToPlay,
  decideArtToPlay,
  decideGMAbilityToUse,
  decideAttackers,
  decideBlockers,
};
